import React, { useEffect, useState } from "react";
import "../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../hooks/useGeneric";

const EMPTY_FORM = {
    agency_id: null,
    agency_name: "",
    email_id: "",
    address1: "",
    address2: "",
    address3: "",
    phone: "",
    mobile: "",
    gst_no: "",
    is_rate_inclusive: "",
    is_mobile_approved: false,
    mobile_valid_upto: "",
    is_ewb_approved: false,
    is_e_invoice_approved: false,
    is_gst_approved: false,
    is_sync_approved: false,
    sync_valid_upto: "",
    amc_valid_upto: "",
    place: "",
    pincode: "",
    client_nos: 0,
    amc_amt: 0,
    api_balance: 0,
    installed_on: "",
    activated_on: "",
    last_updated: "",
    last_opened: "",
    owner_id: "",
    partner_id: "",
};

const Clients = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const { data: agencies = [], loading, fetchAll: fetchAgencies, save: saveAgency, remove: deleteAgency } = useGeneric({
        tableName: "agencies",
        idColumn: "agency_id",
        orderBy: "agency_name",
        removeOnInsert: ["agency_id"],
    });

    const { data: partners = [], fetchAll: fetchPartners } = useGeneric({
        tableName: "partners",
        idColumn: "partner_id",
        orderBy: "partner_name",
    });

    const { data: owners = [], fetchAll: fetchOwners } = useGeneric({
        tableName: "owners",
        idColumn: "owner_id",
        orderBy: "owner_name",
    });


    useEffect(() => {
        fetchAgencies();
        fetchOwners();
        fetchPartners();
    }, [fetchAgencies, fetchOwners, fetchPartners]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ──────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        // Agency Name
        if (!form.agency_name.trim())
            errs.agency_name = "Client name is required.";
        else if (form.agency_name.length > 50)
            errs.agency_name = "Max 50 characters.";

        // Owner
        if (!form.owner_id || form.owner_id === "" || form.owner_id === 0)
            errs.owner_id = "Owner is required.";

        // Partner
        if (!form.partner_id || form.partner_id === "" || form.partner_id === 0)
            errs.partner_id = "Partner is required.";

        // Address (at least one must have content)
        if (!form.address1?.trim() && !form.address2?.trim() && !form.address3?.trim())
            errs.address1 = "At least one address line is required.";

        // Place
        if (!form.place?.trim())
            errs.place = "Place is required.";

        // Pincode
        if (!form.pincode?.trim())
            errs.pincode = "Pincode is required.";
        else if (form.pincode.length !== 6)
            errs.pincode = "Pincode must be 6 digits.";

        // Phone or Mobile (at least one)
        if (!form.phone?.trim() && !form.mobile?.trim())
            errs.phone = "Phone or Mobile is required.";

        // Email format (optional but must be valid if provided)
        if (form.email_id && !/^\S+@\S+\.\S+$/.test(form.email_id))
            errs.email_id = "Invalid email format.";

        // AMC Date
        if (!form.amc_valid_upto)
            errs.amc_valid_upto = "AMC date is required.";
        else if (isNaN(new Date(form.amc_valid_upto).getTime()))
            errs.amc_valid_upto = "Invalid AMC date.";

        // Installed Date
        if (!form.installed_on)
            errs.installed_on = "Installed date is required.";
        else if (isNaN(new Date(form.installed_on).getTime()))
            errs.installed_on = "Invalid installed date.";

        // Activated Date
        if (!form.activated_on)
            errs.activated_on = "Activated date is required.";
        else if (isNaN(new Date(form.activated_on).getTime()))
            errs.activated_on = "Invalid activated date.";

        // Client Nos range
        if (form.client_nos && Number(form.client_nos) > 32767)
            errs.client_nos = "Must be within smallint range.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save (insert or update) ──────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await saveAgency(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (agency) => {
        const toDate = (val) => val ? new Date(val).toISOString().split("T")[0] : "";
        setForm({
            ...agency,
            installed_on: toDate(agency.installed_on),
            activated_on: toDate(agency.activated_on),
            amc_valid_upto: toDate(agency.amc_valid_upto),
            mobile_valid_upto: toDate(agency.mobile_valid_upto),
            sync_valid_upto: toDate(agency.sync_valid_upto),
        });
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this record?")) {
            deleteAgency(id);
        }
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (agencies || []).filter(
        (a) =>
            a.agency_name?.toLowerCase().includes(search.toLowerCase()) ||
            a.phone?.includes(search) ||
            a.place?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Client Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Agency">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search clients..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchAgencies} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>Client ID</th>
                                    <th>Client Name</th>
                                    <th>Address 1</th>
                                    <th>Address 2</th>
                                    <th>Address 3</th>
                                    <th>Phone</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="cl-empty">
                                            No agencies found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((a, i) => (
                                        <tr key={a.agency_id ?? i} className={i % 2 === 0 ? "cl-row-even" : "cl-row-odd"}>
                                            <td>{a.agency_id}</td>
                                            <td>{a.agency_name}</td>
                                            <td>{a.address1}</td>
                                            <td>{a.address2}</td>
                                            <td>{a.address3}</td>
                                            <td>{a.phone}</td>
                                            <td className="cl-actions-td">
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(a)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={() => handleDelete(a.agency_id)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" onClick={closeModal} />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.agency_id ? "Edit Client" : "Add Client"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">

                                {/* Row 1: Agency Name | Agency ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Client Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.agency_name ? " cl-input-error" : ""}`}
                                            placeholder="Agency Name"
                                            maxLength={50}
                                            value={form.agency_name}
                                            onChange={(e) => set("agency_name", e.target.value)}
                                        />
                                        {errors.agency_name && (
                                            <span className="cl-error-msg">{errors.agency_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Client ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.agency_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                {/* Row: Owner | Partner */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Owner</label>
                                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <select
                                                className={`cl-form-control cl-select${errors.owner_id ? " cl-input-error" : ""}`}
                                                style={{ flex: 1 }}
                                                value={form.owner_id ?? ""}
                                                onChange={(e) => set("owner_id", e.target.value)}
                                            >
                                                <option value="">— Select Owner —</option>
                                                {owners.map((o, i) => (
                                                    <option key={o.owner_id ?? i} value={o.owner_id}>
                                                        {o.owner_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="cl-refresh-icon-btn"
                                                onClick={fetchOwners}
                                                title="Refresh Owners"
                                            >
                                                <RefreshCw size={14} />
                                            </button>
                                        </div>
                                        {errors.owner_id && <span className="cl-error-msg">{errors.owner_id}</span>}
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Partner</label>
                                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <select
                                                className={`cl-form-control cl-select${errors.partner_id ? " cl-input-error" : ""}`}
                                                style={{ flex: 1 }}
                                                value={form.partner_id ?? ""}
                                                onChange={(e) => set("partner_id", e.target.value)}
                                            >
                                                <option value="">— Select Partner —</option>
                                                {partners.map((p, i) => (
                                                    <option key={p.partner_id ?? i} value={p.partner_id}>
                                                        {p.partner_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="cl-refresh-icon-btn"
                                                onClick={fetchPartners}
                                                title="Refresh Partners"
                                            >
                                                <RefreshCw size={14} />
                                            </button>
                                        </div>
                                        {errors.partner_id && <span className="cl-error-msg">{errors.partner_id}</span>}
                                    </div>
                                </div>

                                {/* Row: Address 1 | Address 2 | Address 3 */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 1</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.address1 ? " cl-input-error" : ""}`}
                                            placeholder="Address 1"
                                            maxLength={50}
                                            value={form.address1}
                                            onChange={(e) => set("address1", e.target.value)}
                                        />
                                        {errors.address1 && <span className="cl-error-msg">{errors.address1}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 2</label>
                                        <input
                                            type="text"
                                            className="cl-form-control"
                                            placeholder="Address 2"
                                            maxLength={50}
                                            value={form.address2}
                                            onChange={(e) => set("address2", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 3</label>
                                        <input
                                            type="text"
                                            className="cl-form-control"
                                            placeholder="Address 3"
                                            maxLength={50}
                                            value={form.address3}
                                            onChange={(e) => set("address3", e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Row: GST No | Place | Pincode */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">GST No.</label>
                                        <input
                                            type="text"
                                            className="cl-form-control"
                                            placeholder="GST No."
                                            maxLength={50}
                                            value={form.gst_no}
                                            onChange={(e) => set("gst_no", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Place</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.place ? " cl-input-error" : ""}`}
                                            placeholder="Place"
                                            maxLength={50}
                                            value={form.place}
                                            onChange={(e) => set("place", e.target.value)}
                                        />
                                        {errors.place && <span className="cl-error-msg">{errors.place}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Pincode</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.pincode ? " cl-input-error" : ""}`}
                                            placeholder="Pincode"
                                            maxLength={6}
                                            value={form.pincode}
                                            onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))}
                                        />
                                        {errors.pincode && <span className="cl-error-msg">{errors.pincode}</span>}
                                    </div>
                                </div>

                                {/* Row: Phone | Mobile | Email */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Phone</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.phone ? " cl-input-error" : ""}`}
                                            placeholder="Phone"
                                            maxLength={20}
                                            value={form.phone}
                                            onChange={(e) => set("phone", e.target.value)}
                                        />
                                        {errors.phone && <span className="cl-error-msg">{errors.phone}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Mobile</label>
                                        <input
                                            type="text"
                                            className="cl-form-control"
                                            placeholder="Mobile"
                                            maxLength={20}
                                            value={form.mobile}
                                            onChange={(e) => set("mobile", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Email</label>
                                        <input
                                            type="email"
                                            className={`cl-form-control${errors.email_id ? " cl-input-error" : ""}`}
                                            placeholder="Email"
                                            maxLength={50}
                                            value={form.email_id}
                                            onChange={(e) => set("email_id", e.target.value)}
                                        />
                                        {errors.email_id && <span className="cl-error-msg">{errors.email_id}</span>}
                                    </div>
                                </div>

                                {/* Row: Client Nos | AMC Amt | API Balance | AMC Date */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Client Nos</label>
                                        <input
                                            type="number"
                                            className={`cl-form-control${errors.client_nos ? " cl-input-error" : ""}`}
                                            placeholder="Client Nos"
                                            value={form.client_nos ?? ""}
                                            onChange={(e) => set("client_nos", e.target.value)}
                                        />
                                        {errors.client_nos && <span className="cl-error-msg">{errors.client_nos}</span>}
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">AMC Amt</label>
                                        <input
                                            type="number"
                                            className="cl-form-control"
                                            placeholder="AMC Amt"
                                            value={form.amc_amt ?? ""}
                                            onChange={(e) => set("amc_amt", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">API Balance</label>
                                        <input
                                            type="number"
                                            className="cl-form-control"
                                            placeholder="API Balance"
                                            value={form.api_balance ?? ""}
                                            onChange={(e) => set("api_balance", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">AMC Date</label>
                                        <input
                                            type="date"
                                            className={`cl-form-control${errors.amc_valid_upto ? " cl-input-error" : ""}`}
                                            value={form.amc_valid_upto ?? ""}
                                            onChange={(e) => set("amc_valid_upto", e.target.value)}
                                        />
                                        {errors.amc_valid_upto && <span className="cl-error-msg">{errors.amc_valid_upto}</span>}
                                    </div>
                                </div>

                                {/* Row: Installed Date | Activated Date | Is Mobile | Is Synchronise */}
                                <div className="cl-form-group cl-row" style={{ alignItems: "flex-start" }}>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Installed Date</label>
                                        <input
                                            type="date"
                                            className={`cl-form-control${errors.installed_on ? " cl-input-error" : ""}`}
                                            value={form.installed_on ?? ""}
                                            onChange={(e) => set("installed_on", e.target.value)}
                                        />
                                        {errors.installed_on && <span className="cl-error-msg">{errors.installed_on}</span>}
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Activated Date</label>
                                        <input
                                            type="date"
                                            className={`cl-form-control${errors.activated_on ? " cl-input-error" : ""}`}
                                            value={form.activated_on ?? ""}
                                            onChange={(e) => set("activated_on", e.target.value)}
                                        />
                                        {errors.activated_on && <span className="cl-error-msg">{errors.activated_on}</span>}
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="cl-checkbox"
                                                checked={!!form.is_mobile_approved}
                                                onChange={(e) => set("is_mobile_approved", e.target.checked)}
                                            />
                                            Is Mobile
                                        </label>
                                        <input
                                            type="date"
                                            className="cl-form-control"
                                            value={form.mobile_valid_upto ?? ""}
                                            disabled={!form.is_mobile_approved}
                                            onChange={(e) => set("mobile_valid_upto", e.target.value)}
                                        />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="cl-checkbox"
                                                checked={!!form.is_sync_approved}
                                                onChange={(e) => set("is_sync_approved", e.target.checked)}
                                            />
                                            Is Synchronise
                                        </label>
                                        <input
                                            type="date"
                                            className="cl-form-control"
                                            value={form.sync_valid_upto ?? ""}
                                            disabled={!form.is_sync_approved}
                                            onChange={(e) => set("sync_valid_upto", e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Row: Is eWay Bill | Is eInvoice | Is GST */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="cl-checkbox"
                                                checked={!!form.is_ewb_approved}
                                                onChange={(e) => set("is_ewb_approved", e.target.checked)}
                                            />
                                            Is eWay Bill
                                        </label>
                                    </div>
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="cl-checkbox"
                                                checked={!!form.is_e_invoice_approved}
                                                onChange={(e) => set("is_e_invoice_approved", e.target.checked)}
                                            />
                                            Is eInvoice
                                        </label>
                                    </div>
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="cl-checkbox"
                                                checked={!!form.is_gst_approved}
                                                onChange={(e) => set("is_gst_approved", e.target.checked)}
                                            />
                                            Is GST
                                        </label>
                                    </div>
                                </div>

                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.agency_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
